import {
  useId, useLayoutEffect, useMemo, useRef, useState,
  type CSSProperties, type PointerEvent, type ReactNode,
} from "react";
import {
  buildDisplacementMap, buildSquircleMask, grainTexture, supportsBackdropSvgFilter,
} from "./displacementMap";

/** Bredden på fasettkanten. Midten skal være flat og rolig. */
const BEZEL = 34;
/** Største forskyvning i piksler. Spesifikasjonen anbefaler 40–70 for ark. */
const MAX_DISPLACEMENT = 44;
/**
 * Hvor mye rødt og blått brytes ulikt. 1 er ingen dispersjon.
 * Holdes lavt: en synlig fargesaum leses som en renderingsfeil, ikke som glass.
 */
const DISPERSION = 0.94;

const canRefract = supportsBackdropSvgFilter();

/** Hvilken kant den graderte uskarpheten er sterkest mot. */
export type GlassGrade = "top" | "left" | "none";

export interface LiquidGlassProps {
  readonly className?: string;
  readonly style?: CSSProperties | undefined;
  /** Kanten der flaten møter innholdet. Der er uskarpheten sterkest. */
  readonly grade?: GlassGrade;
  readonly children: ReactNode;
  onPointerDown?(e: PointerEvent<HTMLDivElement>): void;
  onPointerMove?(e: PointerEvent<HTMLDivElement>): void;
  onPointerUp?(e: PointerEvent<HTMLDivElement>): void;
  onPointerCancel?(e: PointerEvent<HTMLDivElement>): void;
}

/**
 * En flate av Liquid Glass.
 *
 * Materialet er bygd av lag, ikke av én effekt:
 *
 * 1. **Form.** Kontinuerlige hjørner, tegnet som maske slik at de virker i
 *    alle nettlesere. Rene sirkelbuer er en anti-pattern.
 * 2. **Brytning.** Et fortrengningskart bøyer bakgrunnen ved kanten. Finnes
 *    bare der motoren kan bruke SVG-filtre som `backdrop-filter`.
 * 3. **Gradert uskarphet.** Sterkest der flaten møter innholdet, avtagende
 *    innover — dybde også uten brytning.
 * 4. **Tint og korn.** En farge som blander seg med det bak, og et nesten
 *    usynlig korn som gir overflate i stedet for plast.
 * 5. **Fasett og høylys.** Innvendig skrå kant som gir tykkelse, og en rettet
 *    strek som følger lyskilden.
 * 6. **Skjær ved berøring.** Flaten energiseres under fingeren.
 *
 * Brukes bare på flater som svever over innholdet. Aldri på innhold, og
 * aldri glass oppå glass.
 */
export function LiquidGlass({
  className = "", style, grade = "none", children, ...handlers
}: LiquidGlassProps): JSX.Element {
  const host = useRef<HTMLDivElement | null>(null);
  const rawId = useId();
  const filterId = `lg${rawId.replace(/[^a-zA-Z0-9]/g, "")}`;

  const [map, setMap] = useState<{ url: string; w: number; h: number } | null>(null);
  const [shape, setShape] = useState<string | null>(null);

  const grain = useMemo(grainTexture, []);

  useLayoutEffect(() => {
    const el = host.current;
    if (el === null) return;

    let frame = 0;
    let last = "";

    const measure = (): void => {
      /*
       * `offsetWidth` og ikke `getBoundingClientRect()`: rektangelet tar med
       * skalering fra foreldre, og flatene animeres med transform.
       */
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      if (w < 8 || h < 8) return;
      const cs = getComputedStyle(el);
      const radii = {
        topLeft: parseFloat(cs.borderTopLeftRadius) || 0,
        topRight: parseFloat(cs.borderTopRightRadius) || 0,
        bottomRight: parseFloat(cs.borderBottomRightRadius) || 0,
        bottomLeft: parseFloat(cs.borderBottomLeftRadius) || 0,
      };
      const key = `${w}x${h}:${Object.values(radii).join(",")}`;
      if (key === last) return;
      last = key;

      setShape(buildSquircleMask(w, h, radii));

      if (!canRefract) return;
      // Kartet bygges utenom den kritiske banen, slik at flaten rekker å
      // gli inn før tunge pikseloperasjoner starter.
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const url = buildDisplacementMap({
          width: w, height: h,
          radius: Math.max(radii.topLeft, radii.topRight, radii.bottomRight, radii.bottomLeft),
          bezel: BEZEL, maxDisplacement: MAX_DISPLACEMENT,
        });
        if (url !== null) setMap({ url, w, h });
      });
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => {
      cancelAnimationFrame(frame);
      ro.disconnect();
    };
  }, []);

  const refracting = canRefract && map !== null;

  const vars: Record<string, string> = { "--lg-grain": grain };
  if (refracting) vars["--lg-filter"] = `url(#${filterId})`;
  if (shape !== null) vars["--lg-shape"] = shape;

  return (
    <div
      ref={host}
      className={[
        "lg",
        refracting ? "lg--refract" : "lg--flat",
        shape !== null ? "lg--shaped" : "",
        `lg--grade-${grade}`,
        className,
      ].filter(Boolean).join(" ")}
      style={{ ...style, ...vars } as CSSProperties}
      onPointerDown={handlers.onPointerDown}
      onPointerMove={handlers.onPointerMove}
      onPointerUp={handlers.onPointerUp}
      onPointerCancel={handlers.onPointerCancel}
    >
      {refracting ? (
        <svg className="lg__defs" aria-hidden="true" focusable="false">
          <defs>
            {/*
              `color-interpolation-filters="sRGB"` er ikke valgfritt. Uten den
              tolkes kartet i lineært lysrom, 128 slutter å bety «ingen
              forskyvning», og hele flaten vrir seg.
            */}
            <filter
              id={filterId}
              filterUnits="userSpaceOnUse"
              x="0" y="0" width={map.w} height={map.h}
              colorInterpolationFilters="sRGB"
            >
              <feImage
                href={map.url}
                x="0" y="0" width={map.w} height={map.h}
                preserveAspectRatio="none"
                result="kart"
              />
              {/*
                Dispersjon: glass bryter korte bølgelengder sterkere enn
                lange, så kanten får en svak fargesaum. Fortrengningen kjøres
                én gang per kanal med litt ulik styrke.
              */}
              <feDisplacementMap
                in="SourceGraphic" in2="kart" result="rDisp"
                scale={MAX_DISPLACEMENT * DISPERSION}
                xChannelSelector="R" yChannelSelector="G"
              />
              <feDisplacementMap
                in="SourceGraphic" in2="kart" result="gDisp"
                scale={MAX_DISPLACEMENT}
                xChannelSelector="R" yChannelSelector="G"
              />
              <feDisplacementMap
                in="SourceGraphic" in2="kart" result="bDisp"
                scale={MAX_DISPLACEMENT * (2 - DISPERSION)}
                xChannelSelector="R" yChannelSelector="G"
              />
              <feColorMatrix
                in="rDisp" type="matrix" result="rOnly"
                values="1 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 1 0"
              />
              <feColorMatrix
                in="gDisp" type="matrix" result="gOnly"
                values="0 0 0 0 0  0 1 0 0 0  0 0 0 0 0  0 0 0 1 0"
              />
              <feColorMatrix
                in="bDisp" type="matrix" result="bOnly"
                values="0 0 0 0 0  0 0 0 0 0  0 0 1 0 0  0 0 0 1 0"
              />
              <feBlend in="rOnly" in2="gOnly" mode="screen" result="rg" />
              <feBlend in="rg" in2="bOnly" mode="screen" />
            </filter>
          </defs>
        </svg>
      ) : null}

      <span className="lg__grade" aria-hidden="true" />
      <span className="lg__tint" aria-hidden="true" />
      {children}
    </div>
  );
}
